import { useMemo, useState, type ReactElement } from 'react';

import { Alert, Button, Input, Select } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import {
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import {
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} from '../hooks/useProjectMutations.js';
import { useProjects } from '../hooks/useProjects.js';
import type { ProjectStatus } from '../api/projects.client.js';
import { ProjectFormModal } from '../components/ProjectFormModal.js';
import { ProjectsGrid } from '../components/ProjectsGrid.js';
import { getProjectStatusOptions } from '../lib/projectMeta.js';

export default function ProjectsPage(): ReactElement {
  useI18nSubscription();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<{ id: string } | null>(null);

  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [q, statusFilter],
  );
  const { data, isLoading, isError, error, refetch } = useProjects(filters);
  const updateMut = useUpdateProjectMutation();
  const deleteMut = useDeleteProjectMutation();
  const rows = useMemo(() => data ?? [], [data]);
  const mutationError = (updateMut.error ?? deleteMut.error) as Error | null;

  const inProgressCount = useMemo(() => rows.filter((p) => p.status === 'active').length, [rows]);
  const completedCount = useMemo(() => rows.filter((p) => p.status === 'completed').length, [rows]);

  return (
    <div className="animate-fade-in flex h-full flex-col">
      {/* Header Area */}
      <PageHeader
        title={i18n.t('crm.features.features.projects.pages.projectspage.проекты')}
        subtitle={
          <>
            <span data-testid="projects-visible-count">{rows.length}</span>{' '}
            {i18n.tc('crm.counts.projects', rows.length)} ·{' '}
            {i18n.t('crm.features.features.projects.pages.projectspage.подзаголовок')}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setFormOpen(true)}
              style={LIST_CREATE_BUTTON_STYLE}
              data-testid="projects-create-button"
            >
              {i18n.t('crm.features.features.projects.pages.projectspage.новый_проект')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void refetch()}
              style={LIST_ICON_BUTTON_STYLE}
              aria-label={i18n.t('crm.common.reload')}
            />
          </div>
        }
        testId="page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Filter bar — spec D: hairline, M10, контролы 28px. */}
        <div className="crm-radius-m flex w-full shrink-0 items-center gap-3 border border-[var(--ant-color-border-secondary)] bg-[var(--ant-color-bg-container)] px-3.5 py-2">
          <div className="flex-1 max-w-[400px]">
            <Input
              allowClear
              placeholder={i18n.t('crm.features.features.projects.pages.projectspage.поиск')}
              value={q}
              onChange={(event) => setQ(event.target.value)}
              aria-label={i18n.t('crm.features.features.projects.pages.projectspage.поиск')}
              variant="borderless"
              className="crm-radius-m bg-[var(--g-fill)] transition-colors"
              style={{ height: 28, fontSize: 12, padding: '0 10px' }}
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Select
              allowClear
              placeholder={i18n.t(
                'crm.features.features.projects.pages.projectspage.фильтр_по_статусу',
              )}
              options={getProjectStatusOptions()}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              style={{ minWidth: 180, fontSize: 12 }}
              data-testid="projects-status-filter"
            />
          </div>
        </div>

        {/* Мини-KPI: единая hairline-полоса (spec D `.kpis`). */}
        <div
          className="grid grid-cols-3 overflow-hidden"
          style={{
            background: 'var(--ant-color-border-secondary)',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 'var(--ant-border-radius-lg, 14px)',
          }}
        >
          {(
            [
              {
                testId: 'projects-summary-total',
                label: i18n.t('crm.features.features.projects.pages.projectspage.всего_проектов'),
                value: rows.length,
              },
              {
                testId: 'projects-summary-active',
                label: i18n.t('crm.features.features.projects.pages.projectspage.в_работе'),
                value: inProgressCount,
              },
              {
                testId: 'projects-summary-completed',
                label: i18n.t('crm.features.features.projects.pages.projectspage.завершено'),
                value: completedCount,
              },
            ] as const
          ).map((cell) => (
            <div
              key={cell.testId}
              className="min-w-0"
              style={{ background: 'var(--ant-color-bg-container)', padding: '12px 16px' }}
            >
              <div
                className="truncate"
                style={{
                  fontSize: 10.5,
                  fontWeight: 550,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {cell.label}
              </div>
              <div
                data-testid={cell.testId}
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: 'var(--ant-color-text)',
                  fontVariantNumeric: 'tabular-nums',
                  margin: '4px 0 2px',
                }}
              >
                {cell.value}
              </div>
            </div>
          ))}
        </div>

        {mutationError ? (
          <Alert type="error" showIcon closable title={mutationError.message} />
        ) : null}

        {isLoading ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietListSkeleton rows={5} />
          </div>
        ) : isError ? (
          <ErrorState
            title={i18n.t(
              'crm.features.features.projects.pages.projectspage.не_удалось_загрузить_проекты',
            )}
            error={error}
          />
        ) : rows.length === 0 ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietEmpty
              text={i18n.t('crm.features.features.projects.pages.projectspage.проектов_нет')}
            />
          </div>
        ) : (
          <ProjectsGrid rows={rows} />
        )}
      </div>

      {formOpen ? <ProjectFormModal open onClose={() => setFormOpen(false)} /> : null}
      {editingProject !== null ? (
        <ProjectFormModal
          open
          project={rows.find((project) => project.id === editingProject.id) ?? null}
          onClose={() => setEditingProject(null)}
        />
      ) : null}
    </div>
  );
}
